import { useEffect, useState } from "react";
import AdminLayout, { PageHeader } from "../../components/admin/AdminLayout";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";

const PAGE_KEYS = ["home", "about", "products", "blogs", "contact", "careers", "clients"];

const empty = { page_key: "home", title: "", description: "", keywords: "", og_image: "", canonical_url: "", robots: "" };

export default function SeoSettings() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => { const { data } = await api.get("/admin/seo"); setItems(data); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...empty, ...it }); setOpen(true); };

  const save = async () => {
    try { await api.put("/admin/seo", form); toast.success("Saved"); setOpen(false); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (key) => {
    if (!confirm("Delete SEO for this page?")) return;
    try { await api.delete(`/admin/seo/${key}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AdminLayout>
      <PageHeader
        title="SEO"
        subtitle="Per-page meta titles, descriptions, and keywords."
        actions={
          <Button onClick={openNew} data-testid="add-seo-btn" className="bg-[#C2410C] hover:bg-[#9A3412] text-white">
            <Plus size={16} className="mr-2" />Add page
          </Button>
        }
      />
      <div className="p-8">
        <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid="seo-table">
          <table className="w-full text-sm">
            <thead className="bg-[#F7F8F6] text-left text-[10px] tracking-[0.2em] uppercase text-[#4B5563]">
              <tr><th className="px-6 py-3">Page</th><th className="px-6 py-3">Meta title</th><th className="px-6 py-3">Description</th><th className="px-6 py-3 text-right">Actions</th></tr>
            </thead>
            <tbody className="divide-y divide-[#E2E8F0]">
              {items.length === 0 && <tr><td colSpan={4} className="px-6 py-10 text-center text-[#4B5563]">No SEO configured.</td></tr>}
              {items.map((it) => (
                <tr key={it.id || it.page_key} data-testid={`seo-row-${it.page_key}`}>
                  <td className="px-6 py-3 font-mono text-xs">{it.page_key}</td>
                  <td className="px-6 py-3 font-medium text-[#1F2937] truncate max-w-xs">{it.title}</td>
                  <td className="px-6 py-3 text-[#4B5563] truncate max-w-md">{it.description}</td>
                  <td className="px-6 py-3 text-right">
                    <button onClick={() => openEdit(it)} data-testid={`edit-${it.page_key}`} className="text-[#4B5563] hover:text-[#C2410C] mr-3"><PencilSimple size={16} /></button>
                    <button onClick={() => remove(it.page_key)} data-testid={`del-${it.page_key}`} className="text-[#4B5563] hover:text-[#EF4444]"><Trash size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="seo-dialog">
          <DialogHeader><DialogTitle>{editing ? `Edit SEO · ${editing.page_key}` : "New SEO entry"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Page key</Label>
              <Input value={form.page_key} onChange={(e) => setForm({ ...form, page_key: e.target.value })} placeholder="e.g., home, about, products" data-testid="seo-key" />
              <p className="text-xs text-[#4B5563]">Suggested: {PAGE_KEYS.join(", ")}</p>
            </div>
            <div className="space-y-2"><Label>Meta title</Label><Input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="seo-title" /></div>
            <div className="space-y-2"><Label>Meta description</Label><Textarea rows={3} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="seo-desc" /></div>
            <div className="space-y-2"><Label>Keywords (comma separated)</Label><Input value={form.keywords || ""} onChange={(e) => setForm({ ...form, keywords: e.target.value })} data-testid="seo-keys" /></div>
            <div className="space-y-2"><Label>OG image URL</Label><Input value={form.og_image || ""} onChange={(e) => setForm({ ...form, og_image: e.target.value })} data-testid="seo-og" /></div>
            <div className="space-y-2"><Label>Canonical URL (optional)</Label><Input value={form.canonical_url || ""} onChange={(e) => setForm({ ...form, canonical_url: e.target.value })} data-testid="seo-canonical" /></div>
            <div className="space-y-2"><Label>Robots</Label><Input value={form.robots || ""} onChange={(e) => setForm({ ...form, robots: e.target.value })} placeholder="index,follow" data-testid="seo-robots" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-seo" className="bg-[#C2410C] hover:bg-[#9A3412]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
